import { Icon } from "@iconify/react";
import { ActionIcon, Button, Group, Indicator } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useLocation, useNavigate } from "@remix-run/react";
import { useCallback, useEffect, useState } from "react";

// eslint-disable-next-line import/order
import {
  GetUserPermissionQuery,
  Permission,
} from "~/graphql/__generated__/types";

// import { countNotification } from "~/graphql/queries/notification";
// import { useSubscription } from "~/lib/websocket/useWebsocket";

import { cn } from "~/lib/tw";
import useBreakPoint from "~/lib/use-break-point";

import ChatwootButton from "../chatwoot/chatwoot-button";
import NotificationDrawer from "../notification/notification-drawer";

import AvatarButton from "./avatar-button";
import Logo from "./logo";
import MenuDrawer from "./menu-drawer";

function TopbarMenu({
  title,
  notificationCount, // employeeId,
  menu,
  user,
  chatwootEnabled = true,
  isSuperadmin,
  isImpersonating,
  impersonationTargetName,
}: {
  title?: string;
  notificationCount?: number;
  menu: Permission[];
  user: GetUserPermissionQuery["me"];
  activeBranch: string;
  chatwootEnabled: boolean;
  isSuperadmin?: boolean;
  isImpersonating?: boolean;
  impersonationTargetName?: string | null;
  // employeeId?: number;
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pageTitle, setPageTitle] = useState<string | React.ReactNode>("");
  const getTitle = useCallback(() => {
    if (title) {
      setPageTitle(title);
      return;
    }
    const docsTitle = document.title.split(" | ")[1];
    if (docsTitle === "undefined") {
      setPageTitle("");
      return;
    }
    if (docsTitle) {
      setPageTitle(docsTitle);
      return;
    }
  }, [title]);

  useEffect(() => {
    getTitle();
  }, [pathname, getTitle]);

  const [
    isNotificationDrawerOpen,
    { open: openNotificationDrawer, close: closeNotificationDrawer },
  ] = useDisclosure(false);

  // useSubscription(
  //   countNotification,
  //   (data) => console.log("socket~count", data),
  //   {
  //     url: "https://hcm-dev.trustmedis.net/be-api-new/graphql",
  //     reconnect: false,
  //     reconnectDelay: 3000,
  //     variables: { employeeId },
  //   },
  // );

  const [isMenuDrawerOpen, { open: openMenuDrawer, close: closeMenuDrawer }] =
    useDisclosure(false);

  const isMobile = useBreakPoint("sm");

  return (
    <div>
      <header
        className={cn(
          "shadow-lg z-10 bg-tm-blue-600 py-3 px-[44px] w-full",
          "flex justify-between items-center",
        )}
      >
        <Group className="bg-white relative rounded-md px-4 py-2 shadow-md">
          <Logo hideIhc={true} className="w-12 md:w-36 mx-auto" />
        </Group>

        <div className="flex gap-3 items-center">
          {isSuperadmin ? (
            <Button
              variant="default"
              leftSection={<Icon icon={"tabler:terminal"} />}
              onClick={() => navigate("/superadmin")}
            >
              Superadmin Console
            </Button>
          ) : null}

          <ActionIcon
            onClick={openMenuDrawer}
            variant="dark"
            color="blue.9"
            size={isMobile ? 32 : 40}
            radius={8}
            className="md:hidden"
          >
            <Icon icon="tabler:menu-2" className="h-4 lg:h-6 w-4 lg:w-6" />
          </ActionIcon>

          {chatwootEnabled ? <ChatwootButton /> : null}
          <Indicator
            label={<span className="text-[9px]">{notificationCount}</span>}
            size={14}
            color="red"
            offset={10}
            disabled={!notificationCount}
          >
            <ActionIcon
              onClick={openNotificationDrawer}
              variant="gradient"
              className="rounded-full"
              aria-label="Gradient action icon"
              gradient={{ from: "#28B87A", to: "#3BC9DB", deg: 180 }}
              size={isMobile ? 32 : "xl"}
            >
              <Icon icon="tabler:bell-ringing" className="h-6 w-6" />
            </ActionIcon>
          </Indicator>

          <NotificationDrawer
            open={isNotificationDrawerOpen}
            close={closeNotificationDrawer}
          />

          <MenuDrawer
            open={isMenuDrawerOpen}
            close={closeMenuDrawer}
            menu={menu}
          />

          <AvatarButton
            user={user}
            isImpersonating={isImpersonating}
            impersonationTargetName={impersonationTargetName}
          />
        </div>
      </header>
    </div>
  );
}

export default TopbarMenu;
